<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { UiIcon } from '@shopexperts/shared';
import { announcementBarConfig } from '@/config/announcementBar';

const STORAGE_KEY = 'shopexperts-announcement-dismissed';

const isDismissed = ref(true); // Hidden by default until localStorage is checked
const isReady = ref(false); // Track if localStorage check is complete

const hashString = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
    }
    return `hash-${Math.abs(hash).toString(36)}`;
};

const getAnnouncementId = (): string => {
    const configId = announcementBarConfig.id;
    if (configId) return configId;
    
    const content = announcementBarConfig.content;
    if (content) return hashString(content);
    
    return '';
};

const getDismissedIds = (): string[] => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    } catch {
        return [];
    }
};

const checkIfDismissed = () => {
    const id = getAnnouncementId();
    if (!id) return false;
    return getDismissedIds().includes(id);
};

const dismiss = () => {
    const id = getAnnouncementId();
    if (!id) return;
    
    const dismissedIds = getDismissedIds();
    if (!dismissedIds.includes(id)) {
        dismissedIds.push(id);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dismissedIds));
    }
    isDismissed.value = true;
};

onMounted(() => {
    isDismissed.value = checkIfDismissed();
    isReady.value = true; // Mark as ready after checking localStorage
});

const isVisible = computed(() => 
    isReady.value && // Only show after localStorage check is complete
    announcementBarConfig.enabled && 
    announcementBarConfig.content && 
    !isDismissed.value
);
const content = computed(() => announcementBarConfig.content);
const backgroundColor = computed(() => announcementBarConfig.backgroundColor);
const theme = computed(() => announcementBarConfig.theme);
</script>

<template>
    <div 
        v-if="isVisible"
        class="w-full py-2.5 px-4 text-se-dark dark:text-white"
        :class="theme === 'dark' ? 'dark' : 'light'"
        :style="{ backgroundColor }"
    >
        <div class="container mx-auto flex items-center gap-4">
            <div 
                class="flex-1 text-center prose prose-sm max-w-none prose-p:m-0 prose-a:underline hover:prose-a:no-underline dark:prose-invert"
                v-html="content"
            />
            <button 
                @click="dismiss"
                class="shrink-0 p-1 rounded hover:bg-white/10 transition-colors cursor-pointer"
                aria-label="Dismiss announcement"
            >
                <UiIcon name="close" class="w-4 h-4" />
            </button>
        </div>
    </div>
</template>
